import type { ResourceArticle } from "../types";

// Public editorial adaptation of docs/marketing/Competitor_Research_Pack/09_SIMINO_SOCIAL_VS_SKED_SOCIAL.md
export const article9: ResourceArticle = {
  "slug": "simino-social-vs-sked-social",
  "title": "Simino Social vs Sked Social",
  "description": "Compare Simino Social and Sked Social: pricing research, workflow differences, strengths, and product direction to help you evaluate the right fit.",
  "category": "Comparison",
  "subject": "Sked Social",
  "focus": "Approvals and connected workflows",
  "summary": "Sked Social is a fit when the team needs a mature modern workspace with approvals, listening, inbox, reporting, media workflow, and external-agent connectivity today.",
  "researchDate": "2026-09-20",
  "sourceFile": "09_SIMINO_SOCIAL_VS_SKED_SOCIAL.md",
  "sections": [
    {
      "id": "best-fit",
      "heading": "Best fit",
      "blocks": [
        {
          "type": "paragraph",
          "text": "**Choose Sked Social when:** the team needs a mature modern workspace with approvals, listening, inbox, reporting, media workflow, and external-agent connectivity today."
        },
        {
          "type": "paragraph",
          "text": "**Choose Simino Social when the product vision is realized:** the customer wants a lower-complexity, goal-driven assistant that prepares more of the campaign and keeps approvals central at an SMB-friendly price."
        }
      ]
    },
    {
      "id": "sked-social-s-pricing-and-product-snapshot",
      "heading": "Sked Social’s pricing and product snapshot",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Sked’s official annual-billing page lists:"
        },
        {
          "type": "list",
          "ordered": false,
          "items": [
            "Basic: $24/month, 1 user and 1 social set",
            "Grow: $58/month, 3 users and 6 profiles",
            "Accelerate: $166/month, 6 users and 10 profiles",
            "Custom: from $249/month"
          ]
        },
        {
          "type": "paragraph",
          "text": "Internal approvals cost an additional $500/year on Grow, and external approvals/review portal cost $1,000/year on Grow; both are included in Accelerate. Sked advertises channel scheduling, brand-trained AI captions, ideas and workflows, social listening, a unified inbox, analytics, and MCP connectivity."
        }
      ]
    },
    {
      "id": "where-sked-social-is-stronger",
      "heading": "Where Sked Social is stronger",
      "blocks": [
        {
          "type": "list",
          "ordered": false,
          "items": [
            "Modern, connected social workspace",
            "Strong internal and external approval workflows",
            "No-login client review portal",
            "Cross-channel inbox, analytics, listening, and media handling",
            "Brand-trained AI messaging",
            "MCP support for external AI agents",
            "Clear agency, multi-location, healthcare, and enterprise propositions"
          ]
        }
      ]
    },
    {
      "id": "the-strategic-difference",
      "heading": "The strategic difference",
      "blocks": [
        {
          "type": "table",
          "headers": [
            "Decision area",
            "Sked Social",
            "Simino Social target"
          ],
          "rows": [
            [
              "Starting point",
              "Ideas, drafts, workflows, and calendar",
              "Business goal or promotional outcome"
            ],
            [
              "AI",
              "Brand-trained AI tools and external-agent access through MCP",
              "Native multi-agent operating loop using persistent business and policy context"
            ],
            [
              "Approvals",
              "Mature, priced team/client workflows",
              "Approval as a core trust mechanism, with lightweight reviewer economics"
            ],
            [
              "Analytics",
              "Multi-channel reports and listening",
              "Explanation and next-campaign recommendation for non-specialists"
            ],
            [
              "Customer wedge",
              "Professional teams, agencies, and multi-location brands",
              "SMBs, creators, influencers, and lean agencies"
            ],
            [
              "Influencer workflow",
              "Not the pricing-page center",
              "Planned open quote marketplace connected to campaign goals"
            ]
          ]
        }
      ]
    },
    {
      "id": "why-native-must-mean-more-than-mcp",
      "heading": "Why “native” must mean more than MCP",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Sked demonstrates that external AI-agent access is becoming normal. Simino Social’s advantage has to be the domain state inside the product:"
        },
        {
          "type": "list",
          "ordered": false,
          "items": [
            "Brand facts, voice, offers, approved claims, and restricted claims",
            "Campaign goal, target audience, budget, and success metric",
            "Human roles, approval policy, and audit history",
            "Provider eligibility, connection health, and publishing constraints",
            "Durable jobs, retries, idempotency, and partial-failure recovery",
            "Results tied to the same campaign and future plan"
          ]
        },
        {
          "type": "paragraph",
          "text": "A chatbot or MCP connection can initiate actions; the native product should govern and reliably complete them."
        }
      ]
    },
    {
      "id": "how-simino-social-can-create-more-value-for-money",
      "heading": "How Simino Social can create more value for money",
      "blocks": [
        {
          "type": "list",
          "ordered": false,
          "items": [
            "Keep essential approval available before the customer reaches a high-priced team tier",
            "Allow limited client reviewers without a full seat",
            "Bundle practical profiles for one SMB brand",
            "Use AI to prepare the campaign and explain results rather than sell isolated caption generation",
            "Meter high-cost AI media transparently",
            "Provide simple onboarding for businesses without social operations expertise"
          ]
        }
      ]
    },
    {
      "id": "sources",
      "heading": "Sources",
      "blocks": [
        {
          "type": "list",
          "ordered": false,
          "items": [
            "[Sked Social pricing](https://skedsocial.com/pricing)"
          ]
        }
      ]
    }
  ]
};
